"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type SavedSearch = { id: string; query: string; breed: string; sex: string; createdAt: string; notifications: boolean };
export type BuyerProfile = { name: string; phone: string; region: string; household: string; housingType: string; hasPets: string };
export type ApplicationDraft = { dogId: number | null; workSchedule: string; aloneHours: string; walkPlan: string; careBudget: string; familyAgreement: boolean; notes: string; updatedAt: string | null };

type AppState = {
  favorites: number[];
  recentDogs: number[];
  savedSearches: SavedSearch[];
  buyerProfile: BuyerProfile;
  applicationDraft: ApplicationDraft;
  query: string;
  setQuery: (query: string) => void;
  toggleFavorite: (id: number) => void;
  clearFavorites: () => void;
  addRecentDog: (id: number) => void;
  clearRecentDogs: () => void;
  saveSearch: (search: Omit<SavedSearch, "id" | "createdAt" | "notifications">) => void;
  removeSavedSearch: (id: string) => void;
  toggleSearchNotifications: (id: string) => void;
  updateBuyerProfile: (profile: Partial<BuyerProfile>) => void;
  updateApplicationDraft: (draft: Partial<ApplicationDraft>) => void;
  resetApplicationDraft: (dogId?: number | null) => void;
};

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      favorites: [],
      recentDogs: [],
      savedSearches: [],
      buyerProfile: { name: "", phone: "", region: "", household: "", housingType: "", hasPets: "" },
      applicationDraft: { dogId: null, workSchedule: "", aloneHours: "", walkPlan: "", careBudget: "", familyAgreement: false, notes: "", updatedAt: null },
      query: "",
      setQuery: (query) => set({ query }),
      toggleFavorite: (id) => set((state) => ({
        favorites: state.favorites.includes(id)
          ? state.favorites.filter((item) => item !== id)
          : [...state.favorites, id],
      })),
      clearFavorites: () => set({ favorites: [] }),
      addRecentDog: (id) => set((state) => ({ recentDogs: [id, ...state.recentDogs.filter((item) => item !== id)].slice(0, 12) })),
      clearRecentDogs: () => set({ recentDogs: [] }),
      saveSearch: (search) => set((state) => {
        const duplicate = state.savedSearches.some((item) => item.query === search.query && item.breed === search.breed && item.sex === search.sex);
        return duplicate ? state : { savedSearches: [{ ...search, id: `${Date.now()}`, createdAt: new Date().toISOString(), notifications: false }, ...state.savedSearches].slice(0, 10) };
      }),
      removeSavedSearch: (id) => set((state) => ({ savedSearches: state.savedSearches.filter((item) => item.id !== id) })),
      toggleSearchNotifications: (id) => set((state) => ({ savedSearches: state.savedSearches.map((item) => item.id === id ? { ...item, notifications: !item.notifications } : item) })),
      updateBuyerProfile: (profile) => set((state) => ({ buyerProfile: { ...state.buyerProfile, ...profile } })),
      updateApplicationDraft: (draft) => set((state) => ({ applicationDraft: { ...state.applicationDraft, ...draft, updatedAt: new Date().toISOString() } })),
      resetApplicationDraft: (dogId = null) => set({ applicationDraft: { dogId, workSchedule: "", aloneHours: "", walkPlan: "", careBudget: "", familyAgreement: false, notes: "", updatedAt: null } }),
    }),
    { name: "ondog-preferences" },
  ),
);

type ToastState = { message: string | null; showToast: (message: string) => void; clearToast: () => void };
export const useToastStore = create<ToastState>((set) => ({ message: null, showToast: (message) => set({ message }), clearToast: () => set({ message: null }) }));

type AuthPromptState = { returnTo: string | null; message: string; openAuthPrompt: (returnTo: string, message?: string) => void; closeAuthPrompt: () => void };
export const useAuthPromptStore = create<AuthPromptState>((set) => ({
  returnTo: null,
  message: "로그인이 필요한 기능입니다.",
  openAuthPrompt: (returnTo, message = "로그인이 필요한 기능입니다.") => set({ returnTo, message }),
  closeAuthPrompt: () => set({ returnTo: null }),
}));
